import { useGetEncountersPhotos } from '@/api/get-photos';
import { PageContainer } from '@/components/containers/page-container';
import { Section } from '@/components/containers/section';
import { useEncounter } from '@/contexts/encounter-context';
import { MEDIUM_IMAGE_SIZE } from '@/util/constants';
import { UpdateImageWrapper } from '@/components/uploads/photo-upload/update-photo-wrapper';
import { Loader } from '@/components/loaders/loader';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { Navigate, useNavigate } from '@/util/react-router-dom';
import { Button } from '@/components/button';
import { useTranslation } from '@/contexts/i18n-context';
import { useEffect, useRef, useState } from 'react';
import {
  EncounterStatusIdsType,
  FileStatus,
  GetProviderInfoResponse,
  ReplaceEncounterPhotosErrorType,
} from '@/api/api.schemas';
import styles from './update-images-page.module.css';
import clsx from 'clsx';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { NavButton } from '@/components/navigation/nav-button';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useGetProvider } from '@/api/get-provider-info';
import { Separator } from '@/components/separator';
import { Banner } from '@/components/banner';
import { useUpdateEncountersPhotos } from '@/api/replace-encounter-photos';

const translationBase = 'visit.photoUpload';
const updatePhotosTranslationBase = `${translationBase}.updatePhotos`;

export function UpdateImagesPage() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const [error, setError] = useState<string>();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('--screen-md');

  const { data: provider, isFetching: isFetchingProvider } = useGetProvider(
    encounter.assignedProviderId!,
    { encounterId: encounter.id },
    { query: { enabled: !!encounter.assignedProviderId } },
  );

  const navigate = useNavigate();
  const {
    data: images,
    isFetching,
    refetch,
  } = useGetEncountersPhotos(
    encounter.id,
    {
      width: MEDIUM_IMAGE_SIZE,
      height: MEDIUM_IMAGE_SIZE,
      includeBad: true,
    },
    { query: { placeholderData: (previousData) => previousData ?? { files: [] } } },
  );

  const { mutate: replaceEncounterPhotos, isPending } = useUpdateEncountersPhotos();

  useEffect(() => {
    const badPhotos = images?.files.filter((images) => images.status === FileStatus.Bad) ?? [];

    if (badPhotos.length === 0) {
      setError(undefined);
    }
  }, [images?.files]);

  if (encounter.statusId !== EncounterStatusIdsType.WaitingResponseFromPatient) {
    return <Navigate to="/" />;
  }

  const handleSubmit = () => {
    replaceEncounterPhotos(
      {
        encounterId: encounter.id,
      },
      {
        onSuccess: () => navigate('/'),
        onError: (error) => {
          if (error.type === ReplaceEncounterPhotosErrorType.HasBadPhotos) {
            setError(t(`${updatePhotosTranslationBase}.errors.requiredError`));
            return;
          }

          setError(t('common.errors.unknown'));
        },
      },
    );
  };

  if (isFetchingProvider) {
    return <Loader type="large" />;
  }

  return (
    <PageContainer ref={sectionRef}>
      {!isMobile && (
        <NavButton
          className={styles.backButton}
          type="back"
          text="patientInformation.backToDashboard"
          to="/"
        />
      )}
      <GradientFormTitle withParagraphMargins={false} className={styles.pageTitle}>
        {isMobile && (
          <NavButton
            className={styles.backButton}
            type="back"
            text="patientInformation.backToDashboard"
            to="/"
          />
        )}
        <h1 className="h3">{t(`${updatePhotosTranslationBase}.title`)}</h1>
        <Separator className={styles.separator} />
        <ProviderSection provider={provider} />
      </GradientFormTitle>
      <Section>
        {error && (
          <Banner className={styles.banner} type="error" focusOnMount>
            {error}
          </Banner>
        )}
        <div className={clsx(styles.imagesContainer, styles.divider)}>
          {images?.files.map((photo, index) => (
            <UpdateImageWrapper
              key={photo.id}
              alt={photo.description ?? t(`${translationBase}.photoAlt`, { index: index + 1 })}
              photo={photo}
              onUpdate={refetch}
            />
          ))}
        </div>
        {isFetching && <Loader type="small" />}
      </Section>

      <VisitFormButtonRow
        buttonTextKey="common.confirm"
        onClick={handleSubmit}
        loading={isPending}
        secondaryButton={
          <Button as="a" to="/" variant="outlined" color="accent">
            {t(`${updatePhotosTranslationBase}.backToDashboard`)}
          </Button>
        }
      />
    </PageContainer>
  );
}

interface ProviderSectionProps {
  provider: GetProviderInfoResponse | undefined;
}

function ProviderSection({ provider }: ProviderSectionProps) {
  const { t } = useTranslation();

  if (!provider) {
    return null;
  }

  return (
    <div className={styles.providerSectionContent}>
      {provider.provider.img && (
        <img
          src={provider.provider.img}
          alt={t('svg.providerAvatar')}
          className={styles.providerImg}
        />
      )}
      <div className={styles.providerInfo}>
        <p className={clsx('italic', 'semibold')}>
          {t(`${updatePhotosTranslationBase}.description`)}
        </p>
        <p className={clsx(styles.providerName, 'semibold')}>{provider.provider.fullName}</p>
      </div>
    </div>
  );
}
